import React from "react";
import {
  makeStyles,
  shorthands,
  Button,
  Spinner,
  tokens, mergeClasses
} from "@fluentui/react-components";
import {
  Save20Regular,
  Delete20Regular,
  ArrowLeft20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../risikoanalyseStep/tokens";

export type DetailHeaderBadge = {
  label: string;
  value?: string | React.ReactNode;
  onClick?: () => void;
};

export type DetailHeaderAction = {
  label: string;
  onClick: () => void;
  appearance?: "primary" | "secondary" | "subtle" | "outline" | "transparent";
  icon?: React.ReactElement;
  disabled?: boolean;
};

export interface DetailHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  badges?: DetailHeaderBadge[];
  onBack?: () => void;
  onSave?: () => void;
  onDelete?: () => void;
  isSaving?: boolean;
  isDeleting?: boolean;
  disabled?: boolean;
  /** Extra buttons rendered before Save/Delete (e.g. "Im Formular bearbeiten"). */
  actionButtons?: DetailHeaderAction[];
}

/**
 * Fixed sticky command-bar header shared by the Risk and Contract detail
 * pages. Mirrors the ContactDetails `stickyBar` look (position: sticky;
 * top: 0; backgroundColor #f8f8f8) but is self-contained — no contact-specific
 * badges (relevanz/phase/authority/etc.), just a title, optional badge chips
 * and Zurück / Speichern / Löschen actions.
 */
const useStyles = makeStyles({
  container: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    backgroundColor: "#f8f8f8",
    paddingTop: "10px",
    paddingBottom: "10px",
    ...shorthands.borderBottom("1px", "solid", "rgba(45,20,43,0.08)"),
    "@media (max-width: 768px)": {
      paddingTop: "6px",
      paddingBottom: "8px",
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    columnGap: "16px",
    rowGap: "8px",
    "@media (max-width: 768px)": {
      flexDirection: "column",
    },
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    minWidth: 0,
    flexGrow: 1,
  },
  titleBlock: {
    minWidth: 0,
  },
  title: {
    margin: 0,
    color: BRAND.ink,
    fontFamily: BRAND.fontAccent,
    fontSize: "1.6rem",
    fontWeight: 600,
    lineHeight: 1.2,
    wordBreak: "break-word",
    "@media (max-width: 768px)": {
      fontSize: "1.35rem",
    },
  },
  subtitle: {
    margin: "2px 0 0 0",
    color: BRAND.muted,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
  },
  badgesRow: {
    display: "flex",
    columnGap: "8px",
    rowGap: "6px",
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: "8px",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("4px", "10px"),
    ...shorthands.borderRadius("6px"),
    backgroundColor: tokens.colorNeutralBackground2,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 500,
    color: BRAND.ink,
    whiteSpace: "nowrap",
    userSelect: "none",
  },
  badgeClickable: {
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  badgeLabel: {
    color: BRAND.muted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontSize: "0.7rem",
    fontWeight: 700,
  },
  actionsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    columnGap: "8px",
    rowGap: "8px",
    flexWrap: "wrap",
    flexShrink: 0,
    "@media (max-width: 768px)": {
      width: "100%",
      justifyContent: "flex-start",
    },
  },
});

export const DetailHeader: React.FC<DetailHeaderProps> = ({
  title,
  subtitle,
  icon,
  badges = [],
  onBack,
  onSave,
  onDelete,
  isSaving = false,
  isDeleting = false,
  disabled = false,
  actionButtons = [],
}) => {
  const styles = useStyles();
  const busy = disabled || isSaving || isDeleting;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          {icon && <div style={{ flexShrink: 0 }}>{icon}</div>}
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>{title}</h1>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            {badges.length > 0 && (
              <div className={styles.badgesRow}>
                {badges.map((badge, idx) => (
                  <div
                    key={`${badge.label}-${idx}`}
                    className={mergeClasses(styles.badge, badge.onClick && styles.badgeClickable)}
                    onClick={badge.onClick}
                  >
                    <span className={styles.badgeLabel}>{badge.label}</span>
                    {badge.value != null && <span>{badge.value}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.actionsRow}>
          {onBack && (
            <Button
              appearance="subtle"
              icon={<ArrowLeft20Regular />}
              onClick={onBack}
              disabled={busy}
            >
              Zurück
            </Button>
          )}
          {actionButtons.map((btn, idx) => (
            <Button
              key={`${btn.label}-${idx}`}
              appearance={btn.appearance ?? "secondary"}
              icon={btn.icon}
              onClick={btn.onClick}
              disabled={btn.disabled || busy}
            >
              {btn.label}
            </Button>
          ))}
          {onDelete && (
            <Button
              appearance="secondary"
              icon={isDeleting ? <Spinner size="tiny" /> : <Delete20Regular />}
              onClick={onDelete}
              disabled={busy}
            >
              Löschen
            </Button>
          )}
          {onSave && (
            <Button
              appearance="primary"
              icon={isSaving ? <Spinner size="tiny" /> : <Save20Regular />}
              onClick={onSave}
              disabled={busy}
            >
              Speichern
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailHeader;
